import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  TableContainer,
  IconButton,
  Button,
  Spinner,
  Flex,
  Select,
  Input,
  VStack,
  HStack,
  Badge,
  IconButton as ChakraIconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
} from "@chakra-ui/react";

import { CheckIcon, CloseIcon, SearchIcon, CloseIcon as CloseIconChakra, ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";

import { useHistory } from "react-router-dom";

import AdminHeader from "../AdminPanel/AdminHeader";
import axiosInstance from "../../helpers/axiosInstance";

import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";

const UserForm = () => {
  const toast = useToast();
  const history = useHistory();

  const [userData, setUserData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [approvingEmail, setApprovingEmail] = useState(null);
  const [rejectingEmail, setRejectingEmail] = useState(null);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    type: "",
    subtype: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // Header filter states (newsletter/role-management style)
  const [nameSearchInput, setNameSearchInput] = useState("");
  const [emailSearchInput, setEmailSearchInput] = useState("");
  const [websiteSearchInput, setWebsiteSearchInput] = useState("");
  const [phoneSearchInput, setPhoneSearchInput] = useState("");
  const [dateSort, setDateSort] = useState("newest");
  const [typeSelections, setTypeSelections] = useState([]);
  const [subtypeSelections, setSubtypeSelections] = useState([]);
  const [statusSelections, setStatusSelections] = useState([]);
  const [creatorSubtypeSelections, setCreatorSubtypeSelections] = useState([]);
  const [businessSubtypeSelections, setBusinessSubtypeSelections] = useState([]);

  // Remove showFilters state since filters will always be visible
  // const [showFilters, setShowFilters] = useState(false);

  // Fetch user form data on component mount
  const fetchUserData = async () => {
    try {
      const response = await axiosInstance.get("/userform/userforms");
      setUserData(response.data.user_submissions);
      setFilteredData(response.data.user_submissions);
      setIsLoading(false);
      setIsLoadingUser(false);
    } catch (error) {
      toast({
        title: "Error fetching user data.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
      setIsLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  // Apply filters whenever filters state changes
  useEffect(() => {
    applyFilters();
  }, [filters, userData]);

  // Apply combined header filters as well
  useEffect(() => {
    applyFilters();
  }, [nameSearchInput, emailSearchInput, phoneSearchInput, websiteSearchInput, dateSort, typeSelections, creatorSubtypeSelections, businessSubtypeSelections, statusSelections]);

  const applyFilters = () => {
    let filtered = [...userData];

    // Column header: name/email text search
    if (nameSearchInput) {
      const q = nameSearchInput.toLowerCase();
      filtered = filtered.filter(u => u.name && u.name.toLowerCase().includes(q));
    }
    if (emailSearchInput) {
      const q = emailSearchInput.toLowerCase();
      filtered = filtered.filter(u => u.email && u.email.toLowerCase().includes(q));
    }
    if (phoneSearchInput) {
      const q = phoneSearchInput.toLowerCase();
      filtered = filtered.filter(u => (u.phone !== undefined && u.phone !== null) && u.phone.toString().toLowerCase().includes(q));
    }
    if (websiteSearchInput) {
      const q = websiteSearchInput.toLowerCase();
      filtered = filtered.filter(u => u.websiteOrInstagram && u.websiteOrInstagram.toLowerCase().includes(q));
    }

    // Column header: multi-selects for type/subtype/status
    if (typeSelections.length > 0) {
      filtered = filtered.filter(u => typeSelections.includes(u.type));
    }
    const combinedSubtypes = [...creatorSubtypeSelections, ...businessSubtypeSelections];
    if (combinedSubtypes.length > 0) {
      filtered = filtered.filter(u => combinedSubtypes.includes(u.creator));
    }
    if (statusSelections.length > 0) {
      filtered = filtered.filter(u => {
        const status = u.accepted === null ? "pending" : u.accepted ? "accepted" : "rejected";
        return statusSelections.includes(status);
      });
    }

    // Existing panel filters
    if (filters.type) {
      filtered = filtered.filter(user => 
        user.type && user.type.toLowerCase().includes(filters.type.toLowerCase())
      );
    }

    if (filters.subtype) {
      filtered = filtered.filter(user => 
        user.creator && user.creator.toLowerCase().includes(filters.subtype.toLowerCase())
      );
    }

    if (filters.status) {
      if (filters.status === "pending") {
        filtered = filtered.filter(user => user.accepted === null);
      } else if (filters.status === "accepted") {
        filtered = filtered.filter(user => user.accepted === true);
      } else if (filters.status === "rejected") {
        filtered = filtered.filter(user => user.accepted === false);
      }
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(user => {
        const userDate = new Date(user.application_date);
        const fromDate = new Date(filters.dateFrom);
        return userDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(user => {
        const userDate = new Date(user.application_date);
        const toDate = new Date(filters.dateTo);
        return userDate <= toDate;
      });
    }

    // Sort by application date
    filtered.sort((a, b) => {
      const da = new Date(a.application_date);
      const db = new Date(b.application_date);
      return dateSort === "newest" ? db - da : da - db;
    });

    setFilteredData(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: "",
      subtype: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
    setNameSearchInput("");
    setEmailSearchInput("");
    setPhoneSearchInput("");
    setWebsiteSearchInput("");
    setDateSort("newest");
    setTypeSelections([]);
    setSubtypeSelections([]);
    setStatusSelections([]);
    setCreatorSubtypeSelections([]);
    setBusinessSubtypeSelections([]);
  };

  const getUniqueValues = (field) => {
    const values = userData.map(user => user[field]).filter(Boolean);
    return [...new Set(values)];
  };

  const handleApprove = async (email) => {
    setApprovingEmail(email);
    try {
      const response = await axiosInstance.post("/userform/approve/" + email);
      if (response.status === 201) {
        toast({
          title: "User accepted",
          description: `User "${email}" accepted successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Unable to accept user "${email}"`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setApprovingEmail(null);
      fetchUserData();
    }
  };

  const handleReject = async (email) => {
    setRejectingEmail(email);
    try {
      const response = await axiosInstance.post("/userform/reject/" + email);
      if (response.status === 201) {
        toast({
          title: "User rejected",
          description: `User "${email}" rejected successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "User rejected",
        description: `User "${email}" rejected successfully. Unable to send email.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setRejectingEmail(null);
      fetchUserData();
    }
  };

  const openRemoveDialog = (email) => {
    setRemovingEmail(email);
    setIsRemoveOpen(true);
  };

  const closeRemoveDialog = () => {
    setIsRemoveOpen(false);
    setRemovingEmail(null);
  };

  const handleRemove = async () => {
    if (!removingEmail) return;
    setIsRemoving(true);
    try {
      const payload = { email: removingEmail, role: "User" };
      const response = await axiosInstance.put("/auth/admin/change_role", payload);
      if (response.status === 200) {
        toast({
          title: "User role updated",
          description: `Role set to User for \"${removingEmail}\"`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to update role",
          description: `Could not update role for \"${removingEmail}\"`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || error.message || "Unable to update role.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRemoving(false);
      closeRemoveDialog();
      fetchUserData();
    }
  };

  // Handle file download
  const handleDownload = async () => {
    try {
      const response = await axiosInstance.get("/userform/download", {
        responseType: "blob", // Important for handling files
      });

      const filename = response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/['"]/g, ""); // Extract the filename from the headers

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Use the filename from response
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link); // Clean up after download

      toast({
        title: "Download Successful",
        description: `File "${filename}" has been downloaded.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Error",
        description: "An error occurred while downloading the file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const downloadButton = (
    <IconButton
      aria-label="Download Excel"
      icon={<Box as={DownloadIcon} boxSize="5" />}
      color="green"
      bg="none"
      _hover="none"
      size="lg"
      onClick={handleDownload}
      isLoading={isLoading}
    />
  );

  return (
    <Box h="100vh" w="80vw" bg="white" overflowX="hidden">
      <AdminHeader title="Partner Applications" downloadButton={downloadButton} />

      {/* Filters Panel - Commented out
      <Box p={4} borderBottom="0px" borderColor="gray.200">
        <Flex justify="space-between" align="center" mb={4}>
          <HStack spacing={4} wrap="wrap" flex={1}>
            <Box minW="180px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Type
              </Text>
              <Select
                size="sm"
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                placeholder="All Types"
              >
                {getUniqueValues("type").map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Box>

            <Box minW="180px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Subtype
              </Text>
              <Select
                size="sm"
                value={filters.subtype}
                onChange={(e) => handleFilterChange("subtype", e.target.value)}
                placeholder="All Subtypes"
              >
                {getUniqueValues("creator").map((subtype) => (
                  <option key={subtype} value={subtype}>
                    {subtype}
                  </option>
                ))}
              </Select>
            </Box>

            <Box minW="180px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Status
              </Text>
              <Select
                size="sm"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                placeholder="All Status"
              >
                <option value="pending">Open</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </Select>
            </Box>

            <Box minW="180px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Date From
              </Text>
              <Input
                size="sm"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              />
            </Box>

            <Box minW="180px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Date To
              </Text>
              <Input
                size="sm"
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              />
            </Box>
          </HStack>

          {Object.values(filters).some(filter => filter !== "") && (
            <Button
              size="sm"
              onClick={clearFilters}
              variant="ghost"
              colorScheme="red"
              ml={4}
            >
              Clear All
            </Button>
          )}
        </Flex>

        {Object.values(filters).some(filter => filter !== "") && (
          <Box mt={3}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Active Filters:
            </Text>
            <HStack spacing={2} wrap="wrap">
              {filters.type && (
                <Badge colorScheme="blue" p={2}>
                  Type: {filters.type}
                  <ChakraIconButton
                    size="xs"
                    icon={<CloseIconChakra />}
                    ml={2}
                    onClick={() => handleFilterChange("type", "")}
                    variant="ghost"
                  />
                </Badge>
              )}
              {filters.subtype && (
                <Badge colorScheme="green" p={2}>
                  Subtype: {filters.subtype}
                  <ChakraIconButton
                    size="xs"
                    icon={<CloseIconChakra />}
                    ml={2}
                    onClick={() => handleFilterChange("subtype", "")}
                    variant="ghost"
                  />
                </Badge>
              )}
              {filters.status && (
                <Badge colorScheme="purple" p={2}>
                  Status: {filters.status === "pending" ? "Open" : (filters.status.charAt(0).toUpperCase() + filters.status.slice(1))}
                  <ChakraIconButton
                    size="xs"
                    icon={<CloseIconChakra />}
                    ml={2}
                    onClick={() => handleFilterChange("status", "")}
                    variant="ghost"
                  />
                </Badge>
              )}
              {filters.dateFrom && (
                <Badge colorScheme="orange" p={2}>
                  From: {filters.dateFrom}
                  <ChakraIconButton
                    size="xs"
                    icon={<CloseIconChakra />}
                    ml={2}
                    onClick={() => handleFilterChange("dateFrom", "")}
                    variant="ghost"
                  />
                </Badge>
              )}
              {filters.dateTo && (
                <Badge colorScheme="orange" p={2}>
                  To: {filters.dateTo}
                  <ChakraIconButton
                    size="xs"
                    icon={<CloseIconChakra />}
                    ml={2}
                    onClick={() => handleFilterChange("dateTo", "")}
                    variant="ghost"
                  />
                </Badge>
              )}
            </HStack>
          </Box>
        )}

        <Flex justify="space-between" align="center" mt={4}>
          <Text fontSize="sm" color="gray.600" mb={0}>
            Showing {filteredData.length} of {userData.length} results
          </Text>
        </Flex>
      </Box>
      */}

      {/* Table Section with Scroll */}
      <Box p={4} h="calc(100vh - 200px)" overflowY="auto">
        {isLoading ? (
          <Spinner size="lg" />
        ) : (
          <Box
            maxHeight="calc(100vh - 250px)" // Set the max height for the table area
            overflow="auto" // Enable vertical scrolling if the content exceeds the max height
            // w="75vw"
            w="auto"
          >
            <TableContainer>
              <Table
                cellSpacing={16}
                variant="simple"
                border="1px"
                borderColor="gray.200"
              >
                <Thead>
                  <Tr fontSize="sm">
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Name</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="300px" overflowY="auto">
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Type to search..."
                                value={nameSearchInput}
                                onChange={(e) => setNameSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Email</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="300px" overflowY="auto">
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Type to search..."
                                value={emailSearchInput}
                                onChange={(e) => setEmailSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Phone No.</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="300px" overflowY="auto">
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Type to search..."
                                value={phoneSearchInput}
                                onChange={(e) => setPhoneSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Type</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuGroup title="Select Types">
                              <MenuOptionGroup
                                type="checkbox"
                                value={typeSelections}
                                onChange={setTypeSelections}
                              >
                                {['Business','Creator'].map((t) => (
                                  <MenuItemOption key={t} value={t}>
                                    {t}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuGroup>
                            <MenuDivider />
                            <MenuItem onClick={() => setTypeSelections(['Business','Creator'])}>
                              Select All Types
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Subtype</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="360px" overflowY="auto">
                            <MenuGroup title="Creator Subtypes">
                              <MenuOptionGroup
                                type="checkbox"
                                value={creatorSubtypeSelections}
                                onChange={setCreatorSubtypeSelections}
                              >
                                {['Blogs','Newsletters','Instagram','TikTok','YouTube','Other'].map((s) => (
                                  <MenuItemOption key={s} value={s}>
                                    {s}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                              <MenuItem onClick={() => setCreatorSubtypeSelections(['Blogs','Newsletters','Instagram','TikTok','YouTube','Other'])}>
                                Select All Creator Subtypes
                              </MenuItem>
                            </MenuGroup>
                            <MenuDivider />
                            <MenuGroup title="Business Subtypes">
                              <MenuOptionGroup
                                type="checkbox"
                                value={businessSubtypeSelections}
                                onChange={setBusinessSubtypeSelections}
                              >
                                {['Accomodations','Transportation','Exploration','Food & Drinks','Other'].map((s) => (
                                  <MenuItemOption key={s} value={s}>
                                    {s}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                              <MenuItem onClick={() => setBusinessSubtypeSelections(['Accomodations','Transportation','Exploration','Food & Drinks','Other'])}>
                                Select All Business Subtypes
                              </MenuItem>
                            </MenuGroup>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th maxW="400px">
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Website or <br></br>Social Media Link</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="300px" overflowY="auto">
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Type to search..."
                                value={websiteSearchInput}
                                onChange={(e) => setWebsiteSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Application Date</Text>
                        <Menu>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuItem onClick={() => setDateSort('newest')}>Newest First</MenuItem>
                            <MenuItem onClick={() => setDateSort('oldest')}>Oldest First</MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Status</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuGroup title="Select Status">
                              <MenuOptionGroup
                                type="checkbox"
                                value={statusSelections}
                                onChange={setStatusSelections}
                              >
                                {[
                                  { key: "pending", label: "Open" },
                                  { key: "accepted", label: "Accepted" },
                                  { key: "rejected", label: "Rejected" },
                                ].map((s) => (
                                  <MenuItemOption key={s.key} value={s.key}>
                                    {s.label}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuGroup>
                            <MenuDivider />
                            <MenuItem onClick={() => setStatusSelections(["pending", "accepted", "rejected"]) }>
                              Select All Status
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>Remove as Partner</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((user, index) => (
                      <Tr key={index} fontSize="sm">
                        <Td>{user.name}</Td>
                        <Td>{user.email}</Td>
                        <Td>{user.phone}</Td>
                        <Td>{user.type}</Td>
                        <Td>{user.creator}</Td>
                        <Td maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {user.websiteOrInstagram}
                        </Td>

                        <Td>{user.application_date}</Td>
                        <Td>
                          {user.accepted === null ? (
                            <Flex align="center" gap={4}>
                              <IconButton
                                aria-label="Accept"
                                icon={<CheckIcon />}
                                bg="#286328" // Custom green background
                                color="white"
                                size="md"
                                borderRadius="full"
                                _hover={{ bg: "#1e4f1e" }} // Slightly darker green on hover
                                onClick={() => handleApprove(user.email)}
                                isLoading={approvingEmail === user.email}
                              />
                              <IconButton
                                aria-label="Reject"
                                icon={<CloseIcon />}
                                colorScheme="red"
                                color="white"
                                size="md"
                                borderRadius="full"
                                _hover="none"
                                onClick={() => handleReject(user.email)}
                                isLoading={rejectingEmail === user.email}
                              />
                            </Flex>
                          ) : user.accepted ? (
                            <Text color="#286328" fontWeight="bold" mb={0}>
                              Accepted
                            </Text>
                          ) : (
                            <Text color="red.500" fontWeight="bold" mb={0}>
                              Rejected
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {user.accepted === true && (
                            <IconButton
                              aria-label="Remove"
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              variant="ghost"
                              size="sm"
                              onClick={() => openRemoveDialog(user.email)}
                            />
                          )}
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={9}>No data available</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
      <AlertDialog isOpen={isRemoveOpen} onClose={closeRemoveDialog} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Removal
            </AlertDialogHeader>
                          <AlertDialogBody>
                Are you sure you want to remove <strong>{removingEmail}</strong> as a partner? This action will revoke their elevated access and reset their role to a standard user account.
              </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={closeRemoveDialog}>Cancel</Button>
              <Button colorScheme="red" onClick={handleRemove} ml={3} isLoading={isRemoving}>
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default UserForm;
